/**
 * URL Tracking Parameters
 * Captures, stores, and provides access to marketing/tracking URL parameters
 */

const STORAGE_KEY = 'pfs_tracking_params';

export interface TrackingParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  agent_id?: string;
  variation?: string;
  cid_enc?: string;
  captured_at?: string;
  landing_page?: string;
}

const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'agent_id',
  'variation',
  'cid_enc',
] as const;

/** Normalize A/B variation to uppercase; accepts `a`, `A`, `b`, `B`. */
export function normalizeVariation(
  value: string | undefined | null,
): string | undefined {
  if (!value) return undefined;
  const upper = value.trim().toUpperCase();
  if (upper === "A" || upper === "B") return upper;
  return value;
}

export function isVariationB(
  variation: string | undefined | null,
): boolean {
  return normalizeVariation(variation) === "B";
}

/**
 * Parse tracking parameters from URL
 */
export function parseTrackingParams(url?: string): TrackingParams {
  const searchParams = new URLSearchParams(url ?? window.location.search);
  const params: TrackingParams = {};

  for (const param of TRACKED_PARAMS) {
    const value = searchParams.get(param);
    if (value) {
      params[param] =
        param === "variation" ? normalizeVariation(value) : value;
    }
  }

  return params;
}

/**
 * Check if URL contains any tracking parameters
 */
export function hasTrackingParams(url?: string): boolean {
  const params = parseTrackingParams(url);
  return Object.keys(params).length > 0;
}

/**
 * Save tracking parameters to sessionStorage
 */
export function saveTrackingParams(params: TrackingParams): void {
  if (Object.keys(params).length === 0) return;

  const dataToSave: TrackingParams = {
    ...params,
    captured_at: new Date().toISOString(),
    landing_page: window.location.pathname,
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (e) {
    console.warn('[TrackingParams] Failed to save to sessionStorage:', e);
  }
}

/**
 * Get stored tracking parameters from sessionStorage
 */
export function getStoredTrackingParams(): TrackingParams | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const params = JSON.parse(stored) as TrackingParams;
      if (params.variation) {
        params.variation = normalizeVariation(params.variation);
      }
      return params;
    }
  } catch (e) {
    console.warn('[TrackingParams] Failed to read from sessionStorage:', e);
  }
  return null;
}

/**
 * Clear stored tracking parameters
 */
export function clearTrackingParams(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('[TrackingParams] Failed to clear sessionStorage:', e);
  }
}

/**
 * Capture tracking params from current URL and save them
 * Only saves if params exist and no params are already stored (first touch attribution)
 * @param overwrite - If true, overwrites existing params (last touch attribution)
 */
export function captureTrackingParams(overwrite = false): TrackingParams | null {
  const params = parseTrackingParams();
  
  if (Object.keys(params).length === 0) {
    return getStoredTrackingParams();
  }

  const existingParams = getStoredTrackingParams();
  
  if (!existingParams || overwrite) {
    saveTrackingParams(params);
    return params;
  }

  return existingParams;
}

/**
 * Get tracking params for GA4 (flattened format)
 * Returns params suitable for passing to GA4 events
 */
export function getTrackingParamsForGA4(): Record<string, string> {
  const params = getStoredTrackingParams();
  if (!params) return {};

  const ga4Params: Record<string, string> = {};
  
  if (params.utm_source) ga4Params.utm_source = params.utm_source;
  if (params.utm_medium) ga4Params.utm_medium = params.utm_medium;
  if (params.utm_campaign) ga4Params.utm_campaign = params.utm_campaign;
  if (params.agent_id) ga4Params.agent_id = params.agent_id;
  if (params.variation) ga4Params.variation = normalizeVariation(params.variation);
  if (params.cid_enc) ga4Params.cid_enc = params.cid_enc;
  if (params.landing_page) ga4Params.landing_page = params.landing_page;

  return ga4Params;
}
